import { useTranslate } from "@similarweb/i18n-provider";
import type { ActivationAlertsContractSearchAdFeedGroup } from "@similarweb/pro-api-client";
import { useTracking } from "@similarweb/tracking-provider";
import { Badge, Carousel } from "@similarweb/ui-components";

import { AlertsGroupCarousel } from "../../common/AlertsGroupCarousel";
import { CardLayout } from "../../common/card-layout/CardLayout";
import { ChannelIcon } from "../../common/ChannelIcon";
import { SeeAllButton } from "../../common/SeeAllButton";
import { SeeMoreCard } from "../../common/SeeMoreCard";
import { navigateToSearchAds } from "../utils/navigateToAdsIntel";
import { adsTypeTranslationKeyMap } from "../utils/translationKeysConfig";

import { adCardSize, SearchAdCard } from "./SearchAdCard";

export type SearchAdsAlertProps = {
    country?: number;
    favicon: string;
    group: ActivationAlertsContractSearchAdFeedGroup;
};

export const SearchAdsAlert = ({ group, country, favicon }: SearchAdsAlertProps) => {
    const { track } = useTracking();
    const { translate } = useTranslate();

    const handleAdsPageNavigation = () => {
        track("Alert Card", "click", "open alert/alerts/search-ads");
        navigateToSearchAds({ domain: group.domain, country });
    };

    return (
        <CardLayout
            iconUrl={favicon}
            onClick={handleAdsPageNavigation}
            data-automation="search-ads-alert"
            data-domain={group.domain}
        >
            <CardLayout.Header>
                <Badge data-automation="alert-badge">{translate(adsTypeTranslationKeyMap.Search)}</Badge>
                <CardLayout.Title>
                    {translate("activation.alerts.cards.search-ads.title", {
                        count: group.ads.length,
                        domain: group.domain,
                    })}
                </CardLayout.Title>
                <ChannelIcon channel="Google" />
            </CardLayout.Header>
            <CardLayout.Content>
                <AlertsGroupCarousel>
                    {group.ads.map((item) => (
                        <Carousel.Item key={item.id}>
                            <SearchAdCard
                                description={item.description}
                                firstSeen={item.firstSeen}
                                landingPage={item.landingPage ?? ""}
                                title={item.title}
                            />
                        </Carousel.Item>
                    ))}
                    {group.totalCount > group.ads.length ? (
                        <Carousel.Item key="see-more-card">
                            <SeeMoreCard
                                width={adCardSize.width}
                                height={adCardSize.height}
                                onClick={handleAdsPageNavigation}
                                dataAutomation="search-ad-see-more-card"
                            />
                        </Carousel.Item>
                    ) : null}
                </AlertsGroupCarousel>
                <SeeAllButton
                    onClick={handleAdsPageNavigation}
                    isDisabled={!group.ads.length}
                    dataAutomation="see-all-ads-button"
                >
                    {translate("activation.alerts.cards.ads.see-all")}
                </SeeAllButton>
            </CardLayout.Content>
        </CardLayout>
    );
};
